import * as angular from 'angular';
import {apsViewModels} from "../../shared/dto/APS.ViewModels";
import {apsCommon} from "../../shared/dto/aps.common";
import {DefectType} from "../../models/defectType";
import JobServiceName, {JobService} from "../../services/jobs/job.service";
import DefectTypeServiceName, {DefectTypeService} from "../../services/defects/defectType.service";
import LoaderServiceName, {LoaderService} from "../../components/loader/loader.service";
import NotifyServiceName, {NotifyService} from "../../services/notify/notify.service";
import NewGuidServiceName, {NewGuidService} from "../../services/utility/newguid.service";
import * as _ from "lodash";
import template from './jobEditor.tpl.html';

let requires = [JobServiceName, DefectTypeServiceName, LoaderServiceName, NotifyServiceName, NewGuidServiceName];

interface JobEditorController {
  isAdding: boolean;
  editJob: apsViewModels.JobItem;
  selectedGlassColor: apsCommon.IdAndName;
  selectedWareClass: apsCommon.IdAndName;
  selectedProcess: apsViewModels.JobProcess;

  customers: apsCommon.IdAndName[];
  glassColors: apsCommon.IdAndName[];
  wareClasses: apsCommon.IdAndName[];
  processes: apsViewModels.JobProcess[];

  save: () => void;
  cancel: () => void;

  availableDefectTypes: DefectType[];
  allDefectTypes: DefectType[];
  excludedDefectTypes: DefectType[];
  defectTypeAdded(): void;
  moldChange(): void;
  defectType: string;
  removeDefectType(defectType: DefectType): void;
}

export function openEditJob(data: apsViewModels.JobItem,
                            $modal: mm.foundation.modal): angular.IPromise<apsViewModels.JobItem> {
  return $modal.open({
    template: template,
    controller: 'JobEditorCtrl as vm',
    resolve: {
      data: function() {
        return data;
      }
    }
  }).result;
}

class JobEditorCtlr implements JobEditorController {
  public excludedDefectTypes: DefectType[];
  public availableDefectTypes: DefectType[];
  public defectType: string;
  public isAdding: boolean;
  public editJob: apsViewModels.JobItem;
  form: angular.IFormController;

  public customers: apsCommon.IdAndName[];
  public glassColors: apsCommon.IdAndName[];
  public wareClasses: apsCommon.IdAndName[];
  public processes: apsViewModels.JobProcess[];
  public descriptions: string[];
  public molds: string[];
  public finishes: string[];

  public selectedGlassColor: apsCommon.IdAndName;
  public selectedWareClass: apsCommon.IdAndName;
  public selectedProcess: apsViewModels.JobProcess;
  public selectedPlant: string;
  public selectedShop:string;
  public allDefectTypes: DefectType[];

  private $modalInstance: mm.foundation.modalInstance;
  private jobService: JobService;
  private defectTypeService: DefectTypeService;
  private loaderService: LoaderService;
  private notifyService: NotifyService;
  private newGuidService: NewGuidService;
  private $q: angular.IQService;

  constructor(data: apsViewModels.JobItem,
              $modalInstance: mm.foundation.modalInstance,
              jobService: JobService,
              defectTypeService: DefectTypeService,
              loaderService: LoaderService,
              notifyService: NotifyService,
              newGuidService: NewGuidService,
              $stateParams: any,
              $q: angular.IQService) {
    this.$modalInstance = $modalInstance;
    this.jobService = jobService;
    this.defectTypeService = defectTypeService;
    this.loaderService = loaderService;
    this.notifyService = notifyService;
    this.newGuidService = newGuidService;
    this.$q = $q;

    this.isAdding = !data;
    let promises = [];
    this.excludedDefectTypes = [];
    this.selectedPlant = $stateParams.plantId;
    this.selectedShop = $stateParams.shopId;
    if (this.isAdding) {
      this.editJob = {
        id: '',
        name: '',
        customerId: '',
        customerName: '',
        processId: '',
        processName: '',
        note: '',
        description: '',
        mold: '',
        finish: '',
        wareClassId: '',
        wareClassName: '',
        glassColorId: '',
        glassColorName: '',
        isDeleted: false,
        finishSize: '',
        excludedDefectTypeIds: []
      };

      promises.push(
        this.newGuidService.getNewGuid('job').then((id) => {
          this.editJob.id = id;
        }, this.notifyService.handleApiErrors)
      );

    } else {
      this.editJob = <apsViewModels.JobItem>{ };
      _.assign(this.editJob, data);
    }

    promises.push(
      this.jobService.getCustomers().then((customers) => {
        this.customers = customers;
      }, this.notifyService.handleApiErrors)
    );

    promises.push(
      this.jobService.getProcesses().then((processes) => {
        this.processes = processes;

        if (this.editJob.processId !== '') {
          this.selectedProcess = _.find(this.processes, {id: this.editJob.processId});
        }
      }, this.notifyService.handleApiErrors)
    );

    promises.push(
      this.jobService.getGlassColors().then((glassColors) => {
        this.glassColors = glassColors;

        if (this.editJob.glassColorId !== '') {
          this.selectedGlassColor = _.find(this.glassColors, {id: this.editJob.glassColorId});
        }
      }, this.notifyService.handleApiErrors)
    );

    promises.push(
      this.jobService.getWareClasses().then((wareClasses) => {
        this.wareClasses = wareClasses;

        if (this.editJob.wareClassId !== '') {
          this.selectedWareClass = _.find(this.wareClasses, {id: this.editJob.wareClassId});
        }
      }, this.notifyService.handleApiErrors)
    );

    promises.push(
      this.jobService.getDescriptions().then((descriptions) => {
        this.descriptions = descriptions;
      }, this.notifyService.handleApiErrors)
    );

    promises.push(
      this.jobService.getTargetFinishes(this.selectedPlant,"").then((finishes) => {
        this.finishes = finishes;
      }, this.notifyService.handleApiErrors)
    );

    promises.push(
      this.jobService.getTargetMolds(this.selectedPlant).then((molds) => {
        this.molds = molds;
      }, this.notifyService.handleApiErrors)
    );

    promises.push(
      this.defectTypeService.getDefectTypesAndClassifications()
        .then(data => {
          if (this.editJob.excludedDefectTypeIds && this.editJob.excludedDefectTypeIds.length > 0) {
            this.excludedDefectTypes = _.filter(data.types, dt => _.some(this.editJob.excludedDefectTypeIds, id => id === dt.id));
          }
          this.allDefectTypes = data.types;
          this.availableDefectTypes = _.difference(this.allDefectTypes, this.excludedDefectTypes);
        })
    );

    this.loaderService.loadingPromise(this.$q.all(promises));
  }

  save() {
    if (this.form.$invalid) {
      if(this.form.mold.$invalid || this.form.mold.$dirty){
        const moldVal=this.form.mold;
        if(this.molds.indexOf(moldVal.$viewValue) > -1){
          this.form.$removeControl(this.form.mold);
        }
        if(this.form.$invalid)
        {
          console.log('form invalid', this.form);
          return;
        }
      }else{
      console.log('form invalid', this.form);
      return;
      }
    }

    if (this.selectedProcess) {
      this.editJob.processId = this.selectedProcess.id;
      this.editJob.processName = this.selectedProcess.name;
    }
    if (this.selectedGlassColor) {
      this.editJob.glassColorId = this.selectedGlassColor.id;
      this.editJob.glassColorName = this.selectedGlassColor.name;
    }
    if (this.selectedWareClass) {
      this.editJob.wareClassId = this.selectedWareClass.id;
      this.editJob.wareClassName = this.selectedWareClass.name;
    }

    this.editJob.excludedDefectTypeIds = _.map(this.excludedDefectTypes, 'id');
    let cmd = {
      id: this.editJob.id,
      name: this.editJob.name,
      customerName: this.editJob.customerName,
      processId: this.editJob.processId,
      note: this.editJob.note,
      description: this.editJob.description,
      mold: this.editJob.mold,
      finish: this.editJob.finish,
      finishSize: this.editJob.finishSize,
      wareClassId: this.editJob.wareClassId,
      glassColorId: this.editJob.glassColorId,
      excludedDefectTypeIds: this.editJob.excludedDefectTypeIds,
      sectionChangeStrategyId: null
    };

    let promise: angular.IPromise<any>;
    if (this.isAdding) {
      promise = this.jobService.addJob(cmd);
    } else {
      promise = this.jobService.saveJob(cmd);
    }
    this.loaderService.loadingPromise(
      promise.then(() => {
        this.$modalInstance.close(this.editJob);
      }, this.notifyService.handleApiErrors)
    );
  }

  cancel() {
    this.$modalInstance.close();
  }
  moldChange(): void {
    if(this.editJob.mold)
    {
      this.jobService.getTargetFinishes(this.selectedPlant, this.editJob.mold).then((finishes) => {
        this.finishes = finishes;
      }, this.notifyService.handleApiErrors)
  
    }
  }
  defectTypeAdded(): void {
    let defectType = _.find(this.allDefectTypes, {name: this.defectType});
    if (!defectType) {
      return;
    }

    this.defectType = null;
    this.excludedDefectTypes.push(defectType);
    _.remove(this.availableDefectTypes, defectType);
  };

  removeDefectType(defectType: DefectType): void {
    _.remove(this.excludedDefectTypes, defectType);
    this.availableDefectTypes.push(defectType);
  }
}

export default angular.module('aps.controller.jobedit', requires)
  .controller('JobEditorCtrl', JobEditorCtlr)
  .name;


// WEBPACK FOOTER //
// ./src/app/controllers/job/jobEditor.controller.ts